import styled, {keyframes} from "styled-components";
import {motion} from "framer-motion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SmartImage from "../../lib/UIBookBrd/SmartImage";
import {container, displayHeading, down, eyebrow, focusRing, hideScrollbar} from "../../Styles/media";

/* ------------------------------------------------------------------- shared */

/**
 * The landing page cancels the shell's header offset so the hero runs
 * full-bleed underneath the (transparent) header.
 */
const Page = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: -${({theme}) => theme.header.height}px;

    ${down('md')} {
        margin-top: -${({theme}) => theme.header.heightMobile}px;
    }
`

const Section = styled.section`
    ${container};
    padding-top: clamp(64px, 9vw, 130px);

    ${down('md')} {
        padding-top: 46px;
    }
`

const SectionHead = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 32px;
    margin-bottom: clamp(28px, 4vw, 52px);

    ${down('md')} {
        flex-direction: column;
        align-items: flex-start;
        gap: 18px;
    }
`

const SectionHeadText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-width: 640px;
`

const Eyebrow = styled.span`
    ${eyebrow};
    display: inline-flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        width: 22px;
        height: 1px;
        background-color: ${({theme}) => theme.accent};
    }
`

const SectionTitle = styled.h2`
    ${displayHeading};
    font-size: clamp(30px, 4.4vw, 54px);
`

const SectionLead = styled.p`
    max-width: 56ch;
    font-size: clamp(15px, 1.4vw, 17px);
    line-height: 1.65;
    color: ${({theme}) => theme.colorMuted};
`

const SectionSideAction = styled.div`
    margin-left: auto;
    flex-shrink: 0;

    ${down('md')} {
        margin-left: 0;
    }
`

/* --------------------------------------------------------------------- hero */

const Hero = styled.section`
    position: relative;
    display: flex;
    align-items: flex-end;
    width: 100%;
    min-height: clamp(600px, 100vh, 960px);
    overflow: hidden;
    isolation: isolate;

    ${down('md')} {
        min-height: min(88vh, 720px);
    }
`

const HeroMedia = styled.div`
    position: absolute;
    inset: 0;
    z-index: -2;
`

const HeroVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: grayscale(100%) contrast(112%);
`

const HeroImage = styled(SmartImage)`
    width: 100%;
    height: 100%;
`

const HeroScrim = styled.div`
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(180deg, rgba(8, 8, 10, 0.82) 0%, rgba(8, 8, 10, 0.35) 38%, rgba(8, 8, 10, 0.96) 100%),
    radial-gradient(70% 60% at 20% 80%, rgba(214, 218, 225, 0.10), transparent 70%);
`

const HeroInner = styled.div`
    ${container};
    display: flex;
    flex-direction: column;
    gap: clamp(24px, 3vw, 38px);
    /* Clears the transparent header the hero runs underneath. */
    padding-top: clamp(120px, 16vw, 190px);
    padding-bottom: clamp(38px, 5vw, 64px);

    ${down('md')} {
        gap: 20px;
        padding-top: 104px;
    }
`

const HeroTitle = styled.h1`
    ${displayHeading};
    max-width: 20ch;
    font-size: clamp(42px, 8.4vw, 112px);
`

const HeroTitleLine = styled(motion.span)`
    display: block;
`

const HeroAccent = styled.em`
    font-style: normal;
    color: ${({theme}) => theme.accent};
`

const HeroLead = styled(motion.p)`
    max-width: 52ch;
    font-size: clamp(15px, 1.5vw, 18px);
    line-height: 1.65;
    color: ${({theme}) => theme.colorMuted};
`

const HeroActions = styled(motion.div)`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    ${down('sm')} {
        flex-direction: column;
        align-items: stretch;
    }
`

const HeroStats = styled(motion.dl)`
    display: flex;
    flex-wrap: wrap;
    gap: clamp(20px, 4vw, 56px);
    margin: 0;
    padding-top: clamp(18px, 3vw, 30px);
    border-top: 1px solid ${({theme}) => theme.line};

    ${down('sm')} {
        gap: 16px 28px;
    }

    & > * {
        flex: 0 0 auto;
    }
`

const HeroStat = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const HeroStatValue = styled.dt`
    font-size: clamp(24px, 3vw, 34px);
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${({theme}) => theme.color};
    font-variant-numeric: tabular-nums;
`

const HeroStatLabel = styled.dd`
    ${eyebrow};
    margin: 0;
    font-size: 10.5px;
`

const ScrollCue = styled.div`
    position: absolute;
    right: clamp(18px, 4vw, 48px);
    bottom: clamp(38px, 5vw, 64px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    ${eyebrow};

    ${down('lg')} {
        display: none;
    }
`

const ScrollCueLabel = styled.span`
    writing-mode: vertical-rl;
    letter-spacing: 0.22em;
`

const ScrollCueLine = styled.span`
    width: 1px;
    height: 54px;
    background: linear-gradient(180deg, ${({theme}) => theme.accent}, transparent);
`

/* ---------------------------------------------------------------- gear rail */

const RailViewport = styled.div`
    position: relative;
`

const RailFooter = styled.div`
    display: flex;
    margin-top: 26px;

    ${down('sm')} {
        margin-top: 20px;

        & > * {
            width: 100%;
        }
    }
`

const Rail = styled.div`
    ${hideScrollbar};
    display: flex;
    gap: clamp(12px, 1.4vw, 20px);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    padding-bottom: 6px;
`

const RailCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: clamp(215px, 26vw, 300px);
    scroll-snap-align: start;
    overflow: hidden;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.md};
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.01));
    cursor: pointer;
    transition: border-color 300ms ${({theme}) => theme.ease.out},
    transform 300ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.accentLine};
        transform: translateY(-4px);
    }
`

const RailMedia = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
`

const RailImage = styled(SmartImage)`
    position: absolute;
    inset: 0;
    padding: 16px;
`

const RailBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 0 16px 16px;
`

const RailProducer = styled.span`
    ${eyebrow};
    font-size: 10px;
`

const RailName = styled.span`
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({theme}) => theme.color};
`

const RailPrice = styled.span`
    margin-top: 6px;
    font-size: 14px;
    font-weight: 700;
    color: ${({theme}) => theme.accent};
`

const RailControls = styled.div`
    display: flex;
    gap: 8px;

    ${down('md')} {
        display: none;
    }
`

const RailButton = styled.button`
    ${focusRing};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: 50%;
    color: ${({theme}) => theme.color};
    transition: background-color 220ms ${({theme}) => theme.ease.out}, border-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        background-color: rgba(255, 255, 255, 0.07);
        border-color: ${({theme}) => theme.lineStrong};
    }
`

const RailButtonIcon = styled(FontAwesomeIcon)`
    width: 14px;
    height: 14px;
`

/* ------------------------------------------------------------------ marquee */

const scrollLeft = keyframes`
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(-50%);
    }
`

const MarqueeSection = styled.section`
    margin-top: clamp(64px, 9vw, 130px);

    ${down('md')} {
        margin-top: 46px;
    }
    padding: clamp(24px, 3vw, 38px) 0;
    border-top: 1px solid ${({theme}) => theme.line};
    border-bottom: 1px solid ${({theme}) => theme.line};
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.015);
`

const MarqueeTrack = styled.div`
    display: flex;
    width: max-content;
    animation: ${scrollLeft} 34s linear infinite;

    &:hover {
        animation-play-state: paused;
    }
`

const MarqueeGroup = styled.div`
    display: flex;
    align-items: center;
    gap: clamp(30px, 5vw, 72px);
    padding-right: clamp(30px, 5vw, 72px);
`

const MarqueeItem = styled.span`
    font-size: clamp(17px, 2.4vw, 30px);
    font-weight: 700;
    letter-spacing: -0.02em;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.32);
    transition: color 300ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.color};
    }
`

const MarqueeDot = styled.span`
    width: 6px;
    height: 6px;
    flex-shrink: 0;
    border-radius: 50%;
    background-color: ${({theme}) => theme.accent};
`

/* ------------------------------------------------------------- contact band */

const ContactBand = styled.section`
    ${container};
    padding-top: clamp(64px, 9vw, 130px);
    padding-bottom: clamp(64px, 9vw, 120px);

    ${down('md')} {
        padding-top: 46px;
        padding-bottom: 56px;
    }
`

const ContactPanel = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: clamp(28px, 5vw, 72px);
    padding: clamp(30px, 5vw, 72px);
    overflow: hidden;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.xl};
    background: radial-gradient(120% 140% at 100% 0%, rgba(214, 218, 225, 0.10), transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.015));

    ${down('md')} {
        flex-direction: column;
        align-items: flex-start;
    }
`

const ContactText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const ContactTitle = styled.h2`
    ${displayHeading};
    font-size: clamp(28px, 4.2vw, 52px);
`

const ContactLead = styled.p`
    max-width: 46ch;
    font-size: clamp(15px, 1.4vw, 17px);
    line-height: 1.65;
    color: ${({theme}) => theme.colorMuted};
`

const ContactActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-left: auto;
    flex-shrink: 0;

    ${down('md')} {
        margin-left: 0;
        width: 100%;
    }
`

const ContactLink = styled.a`
    ${focusRing};
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: min(265px, 100%);
    padding: 15px 20px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.md};
    background-color: rgba(8, 8, 10, 0.5);
    font-size: 15px;
    font-weight: 600;
    color: ${({theme}) => theme.color};
    transition: border-color 240ms ${({theme}) => theme.ease.out},
    background-color 240ms ${({theme}) => theme.ease.out},
    transform 240ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.accentLine};
        background-color: ${({theme}) => theme.accentSoft};
        transform: translateY(-2px);
    }
`

const ContactLinkIcon = styled(FontAwesomeIcon)`
    width: 15px;
    height: 15px;
    color: ${({theme}) => theme.accent};
`

const ContactLinkArrow = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
    margin-left: auto;
    color: ${({theme}) => theme.colorFaint};
`

export const Styled = {
    Page,
    Section,
    SectionHead,
    SectionHeadText,
    SectionSideAction,
    Eyebrow,
    SectionTitle,
    SectionLead,

    Hero,
    HeroMedia,
    HeroVideo,
    HeroImage,
    HeroScrim,
    HeroInner,
    HeroTitle,
    HeroTitleLine,
    HeroAccent,
    HeroLead,
    HeroActions,
    HeroStats,
    HeroStat,
    HeroStatValue,
    HeroStatLabel,
    ScrollCue,
    ScrollCueLabel,
    ScrollCueLine,


    RailViewport,
    RailFooter,
    Rail,
    RailCard,
    RailMedia,
    RailImage,
    RailBody,
    RailProducer,
    RailName,
    RailPrice,
    RailControls,
    RailButton,
    RailButtonIcon,

    MarqueeSection,
    MarqueeTrack,
    MarqueeGroup,
    MarqueeItem,
    MarqueeDot,

    ContactBand,
    ContactPanel,
    ContactText,
    ContactTitle,
    ContactLead,
    ContactActions,
    ContactLink,
    ContactLinkIcon,
    ContactLinkArrow
}
